import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/api';
import { setUserContext } from '../services/errorTracking';
import type { SellerInfo, OrganizationInfo, CustomerSession } from '../types/api';
import type { TwilioCredentials } from '../types/twilio';
import type { CommunicationMode } from '../types/app';

interface ScheduledCallData {
  callId: number;
  seller: SellerInfo | null;
  organization: OrganizationInfo;
  scheduledAt?: Date;
  credentials: TwilioCredentials;
  customerSession?: CustomerSession;
  recordingEnabled: boolean; // SPEC-026/028: derived from JoinResponse.recording_enabled (controls transcription consent)
  // Q1: the answer Portal already holds for this customer — true, false, or null
  // when they have not been asked. Source of truth for the consent step; the
  // sessionStorage copy is only an accelerator for a reload of the same tab.
  recordingConsent: boolean | null;
  // W5: the mode the seller is already in, or null when nobody has reported one.
  // Read on the entry screen so a customer arriving second is not the only one in
  // the dark; never used to preselect a mode.
  sellerMode: CommunicationMode | null;
}

interface UseScheduledCallReturn {
  data: ScheduledCallData | null;
  isLoading: boolean;
  error: string | null;
  errorCode: string | null;
  refetch: () => Promise<void>;
}

export function useScheduledCall(roomKey: string | null): UseScheduledCallReturn {
  const [data, setData] = useState<ScheduledCallData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const hasDataRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (!roomKey) {
      return;
    }

    const isInitialLoad = !hasDataRef.current;
    if (isInitialLoad) {
      setIsLoading(true);
    }
    setError(null);
    setErrorCode(null);

    try {
      const response = await apiClient.joinScheduledCall(roomKey);
      const { response: res } = response;

      // Initialize API client token store for automatic token refresh on 401
      if (res.customer_session) {
        apiClient.setTokenStore({
          accessToken: res.customer_session.access_token,
          refreshToken: res.customer_session.refresh_token,
        }, res.customer_session.id);
      }

      setData(prev => {
        // Polling refetch: seller still null → preserve reference to avoid re-renders
        if (prev && prev.seller === null && res.seller === null) {
          return prev;
        }
        // Polling refetch: seller just populated → merge only changed fields,
        // preserve stable references for credentials/customerSession to avoid
        // Twilio reconnect and downstream re-renders
        if (prev && prev.seller === null && res.seller !== null) {
          // W5: a seller who has just been assigned usually has a mode by now —
          // this is the poll that first learns both.
          return {
            ...prev,
            seller: res.seller,
            organization: res.organization,
            sellerMode: res.seller_mode ?? null,
            recordingConsent: res.recording_consent ?? null,
          };
        }
        // Initial load
        return {
          callId: res.call_id,
          seller: res.seller,
          organization: res.organization,
          scheduledAt: res.scheduled_at ? new Date(res.scheduled_at) : undefined,
          credentials: {
            videoToken: res.token,
            chatToken: res.chat_token,
            roomName: res.room_unique_name,
            conversationSid: res.conversation_sid,
          },
          customerSession: res.customer_session,
          recordingEnabled: res.recording_enabled ?? false, // SPEC-026/028: transcription enabled flag
          recordingConsent: res.recording_consent ?? null, // Q1
          sellerMode: res.seller_mode ?? null, // W5
        };
      });
      hasDataRef.current = true;

      if (isInitialLoad) {
        // Set Sentry user context for error tracking (once, on initial load)
        setUserContext({
          callId: res.call_id,
          customerId: res.customer_session?.id,
          roomKey,
        });
      }
    } catch (err) {
      // Check for ApiError shape first (plain object thrown from api.ts request())
      if (
        err !== null &&
        typeof err === 'object' &&
        'error' in err &&
        typeof (err as { error: unknown }).error === 'string' &&
        'status' in err &&
        typeof (err as { status: unknown }).status === 'number'
      ) {
        const apiErr = err as { error: string; status: number; code?: string };
        setError(apiErr.error);
        setErrorCode(apiErr.code ?? null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load appointment');
        setErrorCode(null);
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  }, [roomKey]);

  useEffect(() => {
    if (roomKey) {
      fetchData();
    }
  }, [roomKey, fetchData]);

  return {
    data,
    isLoading,
    error,
    errorCode,
    refetch: fetchData,
  };
}
